<template>
    <!-- IE 2026 11 03 / ODOO-350881 (инвентаризация помощи, этап 1): карточка помощи окна — открывает «?» в шапке окна (Widget.helpCard_open) и в шапке
         приложения (AppTopbar.helpCardPage: страница и её рабочая область). Содержимое и порядок — utils/helpCard.js: описание окна (record_help), блок типа
         (тот же record_help по записи SEGMENTS сегмента объекта и ссылки руководства пользователя), статья окна, навигатор. Пустых заголовков нет: нет описания —
         только блок типа. Живёт в корне приложения, не в body: тёмная тема (filter: invert(1) на корне) достаётся ей сама; полноэкранный виджет — телепорт в него.
         Закрытие — ✕, Esc (window capture с остановкой: App.handleKeydown не закрывает активный виджет), щелчок вне карточки, повторный «?», ссылка -->
    <teleport :to="fsRoot" :disabled="!fsRoot">
        <div v-if="visible" ref="box" class="help-card" role="dialog" :aria-label="title || model.typeName" :data-type="type" :data-id="id" :data-segment="segment"
            :style="{ left: pos.left + 'px', top: pos.top + 'px', visibility: pos.ready ? 'visible' : 'hidden' }" @click.stop>
            <div class="hc-head">
                <i class="pi pi-question-circle hc-head-icon"></i>
                <div class="hc-head-text">
                    <div class="hc-title" :title="title">{{ title || model.typeName }}</div>
                    <div class="hc-sub">{{ model.typeName }}</div>
                </div>
                <button type="button" class="hc-x" :title="t('Close') + ' (Esc)'" @click="hide"><i class="pi pi-times"></i></button>
            </div>
            <div v-if="tabs.length > 1" class="hc-tabs" role="tablist">
                <button v-for="tb in tabs" :key="tb.key" type="button" role="tab" class="hc-tab" :class="{ 'is-active': tb.key === tab }" :aria-selected="tb.key === tab ? 'true' : 'false'"
                    @click="setTab(tb.key)">{{ tb.label }}</button>
            </div>
            <div class="hc-body">
                <div v-if="loading" class="hc-state"><i class="pi pi-spin pi-spinner"></i><span>{{ t('Loading…') }}</span></div>
                <template v-else>
                    <div v-if="model.purpose.length" class="hc-sec hc-purpose">
                        <div class="hc-label">{{ t('What it is for') }}</div>
                        <p v-for="(p, i) in model.purpose" :key="'p' + i" class="hc-text">{{ p }}</p>
                    </div>
                    <div v-if="model.how.length" class="hc-sec hc-how">
                        <div class="hc-label">{{ t('What you can do or check') }}</div>
                        <ul v-if="model.how.length > 1" class="hc-list"><li v-for="(p, i) in model.how" :key="'h' + i">{{ p }}</li></ul>
                        <p v-else class="hc-text">{{ model.how[0] }}</p>
                    </div>
                </template>
                <div class="hc-sec hc-type">
                    <div class="hc-label">{{ model.typeTitle }}</div>
                    <p v-if="!loading && model.typeText" class="hc-text">{{ model.typeText }}</p>
                    <a v-for="l in model.links" :key="l.link" href="#" class="hc-link" :data-link="l.link" @click.prevent="openDoc(l.link)"><i class="pi pi-book"></i><span>{{ l.label }}</span></a>
                </div>
                <div v-if="!loading && (model.article || navigatorOk)" class="hc-sec hc-more">
                    <a v-if="model.article" href="#" class="hc-link hc-article" :data-link="model.article.link" @click.prevent="openDoc(model.article.link, true)"><i class="pi pi-file"></i><span>{{ model.article.label }}</span></a>
                    <a v-if="navigatorOk" href="#" class="hc-link hc-navigator" @click.prevent="openNavigator"><i class="pi pi-compass"></i><span>{{ t('Open the interface navigator') }}</span></a>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { t } from '@/i18n'
import { fullscreenEl } from '@/utils/overlayRoot'
import recordHelp from '@/utils/recordHelp'
import { navigator_on } from '@/utils/navigator'
import { helpCard, helpCard_type, helpCard_items, helpCard_model } from '@/utils/helpCard'

const GAP = 6, MARGIN = 8

export default {
    name: 'HelpCard',
    setup() {
        const store = useMainStore()
        return { store }
    },
    data: () => ({
        visible: false,
        loading: false,
        title: '',
        id: '',
        segment: '',
        mainId: '',
        type: 'form',
        docLink: '',
        tabs: [],          // [{ key, label, target }] — у карточки шапки приложения: страница | рабочая область
        tab: '',
        map: {},           // ответ record_help: { '<segment>|<id>': запись | null }
        pos: { left: 0, top: 0, ready: false },
    }),
    computed: {
        fsRoot() { return fullscreenEl() },
        items() { return helpCard_items({ id: this.id, segment: this.segment, mainId: this.mainId }, this.type) },
        model() { return helpCard_model({ map: this.map, items: this.items, type: this.type, segment: this.segment, docLink: this.docLink, tr: t }) },
        navigatorOk() {
            try { return navigator_on(this.store) && this.store.checkAccess({ command: 'onMenuToggle', viewAccess: 'access-extraView' }) } catch (e) { return false }
        },
    },
    methods: {
        t,
        // opts: { anchor (элемент «?»), vueObj (откуда открывать документацию), title, id, segment, mainId, docLink, type?, tabs? }; повторный «?» того же окна — закрыть
        open(opts) {
            if (this.visible && opts.anchor && opts.anchor === this._anchor) { this.hide(); return false }
            this._anchor = opts.anchor || null
            this._vueObj = opts.vueObj || null
            this.tabs = Array.isArray(opts.tabs) ? opts.tabs.filter(tb => tb && tb.target) : []
            this.tab = this.tabs.length ? this.tabs[0].key : ''
            this._setTarget(this.tabs.length ? this.tabs[0].target : opts)
            this.pos = { left: 0, top: 0, ready: false }
            this.visible = true
            this._listen(true)
            this.load()
            return true
        },
        _setTarget(tg) {
            this.title = String(tg.title || '')
            this.id = String(tg.id ?? '')
            this.segment = String(tg.segment ?? '')
            this.mainId = String(tg.mainId ?? '')
            this.type = tg.type || helpCard_type(this.segment)
            this.docLink = String(tg.docLink || '')
            this.map = {}
        },
        setTab(key) {
            const tb = this.tabs.find(x => x.key === key)
            if (!tb || key === this.tab) return
            this.tab = key
            this._setTarget(tb.target)
            this.load()
        },
        // один вызов record_help; ответ позже закрытия / другого окна отбрасывается
        async load() {
            const token = (this._token = (this._token || 0) + 1)
            this.loading = true
            this.$nextTick(() => this.place())
            const r = await recordHelp.describe(this.items)
            if (token !== this._token || !this.visible) return
            this.map = r.ok && r.map && typeof r.map === 'object' ? r.map : {}
            this.loading = false
            this.$nextTick(() => this.place())
        },
        // под «?» по правому краю, не выходя за окно; места снизу нет — над ним. В тёмной теме fixed отсчитывается от инвертированного корня
        place() {
            const box = this.$refs.box
            if (!this.visible || !box) return
            const vw = window.innerWidth, vh = window.innerHeight
            const a = (this._anchor && this._anchor.isConnected && this._anchor.getClientRects().length) ? this._anchor.getBoundingClientRect()
                : { left: vw / 2, right: vw / 2 + box.offsetWidth / 2, top: 56, bottom: 56 }
            const W = box.offsetWidth, H = box.offsetHeight
            let left = Math.min(Math.max(MARGIN, a.right - W), vw - W - MARGIN)
            let top = a.bottom + GAP
            if (top + H > vh - MARGIN) top = Math.max(MARGIN, Math.min(a.top - H - GAP, vh - H - MARGIN))
            const host = box.parentElement && box.parentElement.closest ? box.parentElement.closest('.dark-theme') : null
            const off = host ? host.getBoundingClientRect() : { left: 0, top: 0 }
            this.pos = { left: Math.round(Math.max(MARGIN, left) - off.left), top: Math.round(top - off.top), ready: true }
        },
        // статья окна (byTab) — как прежний «?» бэка: документация прокручивается к разделу активной вкладки окна (pass_activeTab)
        openDoc(link, byTab = false) {
            const vueObj = this._vueObj || this.store.activeWIDGET?.vueObj || this.store.App
            this.hide()
            if (link && vueObj) this.store.executeStoreMethod(vueObj, { command: 'widget_open', widget: 'widget-docs', params: { doc_link: link }, ...(byTab ? { pass_activeTab: true } : {}) })
        },
        openNavigator() {
            const vueObj = this._vueObj || this.store.App
            this.hide()
            if (vueObj) this.store.executeStoreMethod(vueObj, { command: 'navigator_open' })
        },
        hide() {
            if (!this.visible) return
            this.visible = false
            this.loading = false
            this._token = (this._token || 0) + 1
            this._anchor = null
            this._vueObj = null
            this._listen(false)
        },
        _onKey(e) {
            if (!this.visible || e.key !== 'Escape') return
            e.preventDefault(); e.stopImmediatePropagation()
            this.hide()
        },
        // щелчок вне карточки закрывает её; по самому «?» — нет: повторный «?» закрывает её в open
        _onDown(e) {
            const box = this.$refs.box, tg = e.target
            if (!this.visible || (box && box.contains(tg)) || (this._anchor && this._anchor.contains && this._anchor.contains(tg))) return
            this.hide()
        },
        _listen(on) {
            if (on === !!this._listening) return
            this._listening = on
            const m = on ? 'addEventListener' : 'removeEventListener'
            if (!this._h) this._h = { key: (e) => this._onKey(e), down: (e) => this._onDown(e), resize: () => this.hide() }
            window[m]('keydown', this._h.key, true)
            window[m]('pointerdown', this._h.down, true)
            window[m]('resize', this._h.resize)
        },
    },
    mounted() {
        helpCard.card = this
    },
    beforeUnmount() {
        this._listen(false)
        if (helpCard.card === this) helpCard.card = null
    },
}
</script>

<style>
/* IE 2026 11 03 / ODOO-350881: вид — как диалог описания записи (RecordHelpDialog: белое окно, тень, акцент гаммы --cs-accent); тёмная тема — инверсия корня,
   hue-rotate возвращает тон акцента (как у плиток KPI). Стили не scoped: в полноэкранном виджете карточка телепортируется в него */
.help-card { position: fixed; z-index: 20000; display: flex; flex-direction: column; width: min(400px, calc(100vw - 16px)); max-height: min(560px, calc(100vh - 16px));
    box-sizing: border-box; background: #fff; border: 1px solid #dfe3ea; border-radius: 6px; box-shadow: 0 6px 24px rgba(0, 0, 0, .22); color: #333; font-size: 13px; overflow: hidden; }
.dark-theme .help-card { filter: hue-rotate(180deg); }
.hc-head { display: flex; align-items: flex-start; gap: 10px; padding: 12px 10px 8px 14px; }
.hc-head-icon { color: var(--cs-accent, #647eff); font-size: 1.1rem; margin-top: 2px; }
.hc-head-text { flex: 1 1 auto; min-width: 0; }
.hc-title { font-size: 14px; font-weight: 600; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hc-sub { font-size: 11px; color: #6c757d; margin-top: 1px; }
.hc-x { flex: 0 0 auto; border: none; background: transparent; color: #6c757d; cursor: pointer; padding: 3px 5px; border-radius: 4px; }
.hc-x:hover { color: #d9534f; background: #fdeeee; }
.hc-tabs { display: flex; gap: 2px; padding: 0 14px; border-bottom: 1px solid #dee2e6; }
.hc-tab { border: none; border-bottom: 2px solid transparent; margin-bottom: -1px; background: transparent; padding: 5px 10px; font: inherit; font-size: 12px; color: #6c757d; cursor: pointer; }
.hc-tab.is-active { color: var(--cs-accent, #647eff); border-bottom-color: var(--cs-accent, #647eff); font-weight: 600; }
.hc-body { flex: 1 1 auto; overflow: auto; padding: 4px 14px 12px; }
.hc-sec { padding: 8px 0 2px; }
.hc-sec + .hc-sec { border-top: 1px solid #eef0f4; }
.hc-label { font-size: 11px; font-weight: 600; letter-spacing: .02em; color: #6c757d; text-transform: uppercase; margin-bottom: 4px; }
.hc-text { margin: 0 0 6px; line-height: 1.45; }
.hc-list { margin: 0 0 6px; padding-left: 18px; line-height: 1.45; }
.hc-list li { margin-bottom: 2px; }
.hc-link { display: flex; align-items: center; gap: 7px; padding: 3px 0; color: var(--cs-accent, #647eff); text-decoration: none; line-height: 1.35; }
.hc-link span { text-decoration: underline; text-underline-offset: 2px; }
.hc-link:hover span { text-decoration-thickness: 2px; }
.hc-link .pi { font-size: 12px; }
.hc-state { display: flex; align-items: center; gap: 8px; padding: 10px 0; color: #6c757d; }
</style>
